/**
 * Operator platform prep skill CRUD (`/chat/admin/prep-skills…`).
 */
import type {
  PlatformSkillCreateRequest,
  PlatformSkillDetailWire,
  PlatformSkillListResponse,
  PlatformSkillPatchRequest,
  PlatformSkillSummaryWire,
} from '../types/backend';
import { DOMAIN_ERROR_MESSAGES_DA } from '../types/backend';
import { nowISO } from '../lib/ids';
import { ApiError, USE_LIVE, fetchJson, mapAdminError } from './http';

const MOCK_LAV_EN_TEST_PROMPT =
  'Aktiv skill: Lav en test. Hjælp læreren med at lave en prøve på dansk.';

const ADMIN_LIST_ERROR_MESSAGE = 'Kunne ikke hente skills.';
const ADMIN_CREATE_ERROR_MESSAGE = 'Kunne ikke oprette skillen.';
const ADMIN_UPDATE_ERROR_MESSAGE = 'Kunne ikke gemme skillen.';
const ADMIN_DELETE_ERROR_MESSAGE = 'Kunne ikke slette skillen.';

const MOCK_PROTECTED_ID = 'c8f4a2b1-6d3e-4f89-a7c2-1e5b903d4a6f';

const mockPlatformSkills: PlatformSkillDetailWire[] = [
  {
    id: MOCK_PROTECTED_ID,
    slug: 'lav-en-test',
    title: 'Lav en test',
    description: 'Få hjælp til at lave en prøve eller test til dine elever.',
    status: 'ready',
    system_prompt: MOCK_LAV_EN_TEST_PROMPT,
    updated_at: nowISO(),
    is_protected: true,
  },
];

function mapSummary(wire: PlatformSkillSummaryWire): PlatformSkillSummaryWire {
  return wire;
}

function mapDetail(wire: PlatformSkillDetailWire): PlatformSkillDetailWire {
  return wire;
}

export async function listPlatformSkills(): Promise<PlatformSkillSummaryWire[]> {
  if (!USE_LIVE) {
    return mockPlatformSkills.map((skill) => ({
      slug: skill.slug,
      title: skill.title,
      description: skill.description,
      status: skill.status,
      updated_at: skill.updated_at,
    }));
  }
  try {
    const data = await fetchJson<PlatformSkillListResponse>('/chat/admin/prep-skills', {
      admin: true,
    });
    return data.skills.map(mapSummary);
  } catch (error) {
    throw mapAdminError(error, ADMIN_LIST_ERROR_MESSAGE);
  }
}

export async function getPlatformSkill(slug: string): Promise<PlatformSkillDetailWire> {
  if (!USE_LIVE) {
    const found = mockPlatformSkills.find((skill) => skill.slug === slug);
    if (!found) {
      throw new ApiError({
        httpStatus: 404,
        code: 'skill_not_found',
        userMessage: DOMAIN_ERROR_MESSAGES_DA.skill_not_found,
      });
    }
    return mapDetail({ ...found });
  }
  try {
    const data = await fetchJson<PlatformSkillDetailWire>(
      `/chat/admin/prep-skills/${encodeURIComponent(slug)}`,
      { admin: true },
    );
    return mapDetail(data);
  } catch (error) {
    throw mapAdminError(error, ADMIN_LIST_ERROR_MESSAGE);
  }
}

export async function createPlatformSkill(
  body: PlatformSkillCreateRequest,
): Promise<PlatformSkillDetailWire> {
  if (!USE_LIVE) {
    const detail: PlatformSkillDetailWire = {
      id: `mock-skill-${Date.now()}`,
      slug: body.slug,
      title: body.title,
      description: body.description ?? '',
      status: 'ready',
      system_prompt: body.system_prompt,
      updated_at: nowISO(),
      is_protected: false,
    };
    mockPlatformSkills.push(detail);
    return mapDetail(detail);
  }
  try {
    const data = await fetchJson<PlatformSkillDetailWire>('/chat/admin/prep-skills', {
      method: 'POST',
      admin: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return mapDetail(data);
  } catch (error) {
    throw mapAdminError(error, ADMIN_CREATE_ERROR_MESSAGE);
  }
}

export async function updatePlatformSkill(
  slug: string,
  patch: PlatformSkillPatchRequest,
): Promise<PlatformSkillDetailWire> {
  if (!USE_LIVE) {
    const index = mockPlatformSkills.findIndex((skill) => skill.slug === slug);
    if (index < 0) {
      throw new ApiError({
        httpStatus: 404,
        code: 'skill_not_found',
        userMessage: DOMAIN_ERROR_MESSAGES_DA.skill_not_found,
      });
    }
    const existing = mockPlatformSkills[index];
    if (existing.is_protected) {
      if (patch.slug !== undefined && patch.slug !== existing.slug) {
        throw new ApiError({
          httpStatus: 400,
          code: 'skill_slug_reserved',
          userMessage: DOMAIN_ERROR_MESSAGES_DA.skill_slug_reserved,
        });
      }
      if (
        patch.system_prompt !== undefined
        && patch.system_prompt !== existing.system_prompt
      ) {
        throw new ApiError({
          httpStatus: 400,
          code: 'skill_slug_reserved',
          userMessage: DOMAIN_ERROR_MESSAGES_DA.skill_slug_reserved,
        });
      }
    }
    const next: PlatformSkillDetailWire = {
      ...existing,
      ...patch,
      slug: patch.slug ?? existing.slug,
      title: patch.title ?? existing.title,
      description: patch.description ?? existing.description,
      system_prompt: patch.system_prompt ?? existing.system_prompt,
      updated_at: nowISO(),
    };
    mockPlatformSkills[index] = next;
    return mapDetail(next);
  }
  try {
    const data = await fetchJson<PlatformSkillDetailWire>(
      `/chat/admin/prep-skills/${encodeURIComponent(slug)}`,
      {
        method: 'PATCH',
        admin: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      },
    );
    return mapDetail(data);
  } catch (error) {
    throw mapAdminError(error, ADMIN_UPDATE_ERROR_MESSAGE);
  }
}

export async function deletePlatformSkill(slug: string): Promise<void> {
  if (!USE_LIVE) {
    const index = mockPlatformSkills.findIndex((skill) => skill.slug === slug);
    if (index < 0) {
      throw new ApiError({
        httpStatus: 404,
        code: 'skill_not_found',
        userMessage: DOMAIN_ERROR_MESSAGES_DA.skill_not_found,
      });
    }
    if (mockPlatformSkills[index].is_protected) {
      throw new ApiError({
        httpStatus: 400,
        code: 'skill_slug_reserved',
        userMessage: DOMAIN_ERROR_MESSAGES_DA.skill_slug_reserved,
      });
    }
    mockPlatformSkills.splice(index, 1);
    return;
  }
  try {
    await fetchJson<void>(`/chat/admin/prep-skills/${encodeURIComponent(slug)}`, {
      method: 'DELETE',
      admin: true,
    });
  } catch (error) {
    throw mapAdminError(error, ADMIN_DELETE_ERROR_MESSAGE);
  }
}
